import { useCallback, useEffect, useRef, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';

export const useResizableColumns = <T extends GridColDef>(initialColumns: T[]) => {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const widths: Record<string, number> = {};
    initialColumns.forEach((col) => {
      widths[col.field] = col.width ?? 150;
    });
    return widths;
  });

  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerNode(node);
  }, []);
  const containerNodeRef = useRef<HTMLDivElement | null>(null);
  containerNodeRef.current = containerNode;

  const dragStateRef = useRef<{ field: string; startX: number; startWidth: number } | null>(null);
  const columnWidthsRef = useRef(columnWidths);
  columnWidthsRef.current = columnWidths;

  const fitColumnsToContainer = useCallback(() => {
    const container = containerNodeRef.current;
    if (!container || dragStateRef.current) return;

    const availableWidth = container.clientWidth;
    if (availableWidth <= 0) return;

    const currentWidths = columnWidthsRef.current;
    const totalWidth = Object.values(currentWidths).reduce((sum, w) => sum + w, 0);

    if (totalWidth === 0 || totalWidth === availableWidth) return;

    const scale = availableWidth / totalWidth;
    const scaled: Record<string, number> = {};
    Object.entries(currentWidths).forEach(([field, width]) => {
      scaled[field] = Math.max(50, Math.floor(width * scale));
    });
    setColumnWidths(scaled);
  }, []);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (e.button !== 0) return;
    const separator = (e.target as HTMLElement).closest('.MuiDataGrid-columnSeparator');
    if (!separator) return;

    const headerCell = separator.closest('.MuiDataGrid-columnHeader');
    if (!headerCell) return;

    const field = headerCell.getAttribute('data-field');
    if (!field) return;

    e.preventDefault();
    e.stopPropagation();

    const currentWidth = columnWidthsRef.current[field] ?? 150;
    dragStateRef.current = { field, startX: e.clientX, startWidth: currentWidth };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStateRef.current) return;

    const { field, startX, startWidth } = dragStateRef.current;
    const diff = e.clientX - startX;
    let newWidth = Math.max(50, startWidth + diff);

    const container = containerNodeRef.current;
    if (container) {
      const availableWidth = container.clientWidth;
      const currentWidths = columnWidthsRef.current;
      const otherColumnsWidth = Object.entries(currentWidths)
        .filter(([f]) => f !== field)
        .reduce((sum, [, w]) => sum + w, 0);
      newWidth = Math.min(newWidth, Math.max(50, availableWidth - otherColumnsWidth));
    }

    setColumnWidths((prev) => ({ ...prev, [field]: newWidth }));
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (!containerNode) return;

    containerNode.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      containerNode.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      dragStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [containerNode, handleMouseDown, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (!containerNode) return;

    const observer = new ResizeObserver(() => {
      fitColumnsToContainer();
    });
    observer.observe(containerNode);
    fitColumnsToContainer();

    return () => observer.disconnect();
  }, [containerNode, fitColumnsToContainer]);

  const columns: T[] = initialColumns.map((col) => ({
    ...col,
    width: columnWidths[col.field] ?? col.width ?? 150,
  }));

  return { columns, containerRef, containerNode };
};
